import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import Breadcrumbs from "../../components/marketing/Breadcrumbs";
import ContactForm from "../../components/marketing/ContactForm";
import PracticePagination from "../../components/marketing/PracticePagination";
import Reveal from "../../components/marketing/Reveal";
import { JsonLd, useSeo } from "../../components/marketing/Seo";
import { PRACTICE_REDIRECTS, PRACTICES } from "../../data/marketing";

export default function PracticeDetailPage() {
  const { slug } = useParams();
  const redirect = PRACTICE_REDIRECTS[slug];
  const practice = PRACTICES.find((p) => p.slug === slug);

  useSeo({
    title: practice ? practice.title : "Practice",
    description: practice ? `${practice.tagline} ${practice.summary}` : "CLYMACO practice advisory.",
    path: `/practice/${slug}`,
    image: practice?.image?.startsWith("/") ? `https://clymaco.com${practice.image}` : practice?.image,
  });

  if (redirect) return <Navigate to={`/practice/${redirect}`} replace />;
  if (!practice) return <Navigate to="/practices" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: practice.title,
    provider: { "@type": "Organization", name: "CLYMACO Sustainability Services LLP", url: "https://clymaco.com" },
    description: practice.summary,
    areaServed: ["India", "International"],
    url: `https://clymaco.com/practice/${practice.slug}`,
  };

  return (
    <main id="main" className="bg-parchment-base pt-36" data-testid={`practice-page-${practice.slug}`}>
      <JsonLd data={schema} id={`service-schema-${practice.slug}`} />
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Practices", href: "/practices" }, { label: practice.title }]} />
        <Link to="/practices" className="mb-6 inline-flex items-center gap-2 rounded-full border border-forest-deep/15 bg-white px-4 py-2 text-sm font-bold text-forest-deep transition-colors hover:bg-forest-deep/5" data-testid="practice-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to all practices
        </Link>
        <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-end">
          <div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">{practice.num} · {practice.audience}</div>
          <h1 className="mt-4 font-display text-5xl font-medium tracking-tight text-forest-deep sm:text-6xl" data-testid="practice-title">{practice.title}</h1>
          <p className="mt-4 font-display text-2xl font-medium text-forest-primary">{practice.tagline}</p>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-forest-deep/65">{practice.summary}</p>
        </div>
        <div className="relative overflow-hidden rounded-[2rem] bg-forest-deep shadow-xl shadow-forest-deep/15">
          <img src={practice.image} alt={practice.imageCaption} className="h-[320px] w-full object-cover opacity-90" />
          <span className="absolute right-5 top-5 rounded-full bg-black/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/85">{practice.imageCaption}</span>
        </div>
        </div>
      </section>

      <section className="bg-[#F4EFE6] py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-3">
          <Reveal className="rounded-3xl bg-white p-7">
            <h2 className="font-display text-2xl font-medium text-forest-deep">Who this is for</h2>
            <p className="mt-4 text-sm leading-relaxed text-forest-deep/65">{practice.who}</p>
          </Reveal>
          <Reveal delay={0.05} className="rounded-3xl bg-white p-7">
            <h2 className="font-display text-2xl font-medium text-forest-deep">Problems we help solve</h2>
            <p className="mt-4 text-sm leading-relaxed text-forest-deep/65">{practice.problems}</p>
          </Reveal>
          <Reveal delay={0.1} className="rounded-3xl bg-forest-deep p-7 text-[#FBF9F4]">
            <h2 className="font-display text-2xl font-medium">Consulting + technology</h2>
            <p className="mt-4 text-sm leading-relaxed text-emerald-100/70">{practice.technology}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
        <div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-forest-primary">Scope and typical deliverables</div>
          <h2 className="mt-3 font-display text-3xl font-medium text-forest-deep sm:text-4xl">A defined mandate. Tangible outputs.</h2>
          <div className="mt-8 space-y-3">
            {practice.deliverables.map((item) => (
              <div key={item} className="flex gap-3 rounded-2xl border border-forest-deep/10 bg-white p-4 text-sm leading-relaxed text-forest-deep/70">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-forest-primary" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          {practice.streams && (
            <div className="mt-8 rounded-3xl bg-forest-deep/[0.04] p-6">
              <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-forest-primary">Indian EPR streams covered in scope</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {practice.streams.map((stream) => <span key={stream} className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-forest-deep/70">{stream}</span>)}
              </div>
            </div>
          )}
        </div>
        <div className="lg:sticky lg:top-32 lg:self-start">
          <div className="mb-5 rounded-3xl bg-forest-deep p-7 text-[#FBF9F4]">
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300">Relevant next step</div>
            <h2 className="mt-3 font-display text-2xl font-medium">{practice.nextStep}</h2>
            <p className="mt-3 text-sm leading-relaxed text-emerald-100/70">Tell us the entity, site or programme you want to review. We will respond with an appropriate scope.</p>
          </div>
          <ContactForm defaultTopic={practice.title} compact />
        </div>
      </section>
      <PracticePagination currentSlug={practice.slug} />
    </main>
  );
}
